import { useEffect, useState } from "react";
import { Navigate, useNavigate, useParams, useSearchParams } from "react-router-dom";
import {
  Abas, Alerta, Botao, Caminho, Selo, SeloDeEmpresa, SeletorDeEmpresa,
} from "@plantus/ui";
import type { Aba } from "@plantus/ui";
import { producao } from "@plantus/api-client";
import type { Lote } from "@plantus/api-client";

import { usarEmpresas } from "../../../nucleo/empresa/ProvedorDeEmpresas";
import { caminhoDaRota } from "../../../nucleo/navegacao/caminho";
import { ROTAS } from "../../../nucleo/navegacao/rotas";
import { AbaResumo } from "./AbaResumo";
import { AbaVolumes } from "./AbaVolumes";
import { PaginaEtiquetas } from "./PaginaEtiquetas";
import { ROTULO_DA_SITUACAO, TOM_DA_SITUACAO } from "./situacaoDoLote";

/**
 * As seções que o lote tem — **e só as que têm conteúdo**.
 *
 * <p>O rascunho do PO trazia uma quarta, `Histórico`. Ela **não foi criada**, e
 * a ausência é a decisão: não existe endpoint de histórico de lote (nem caso de
 * uso, nem tela). Uma aba vazia se lê como sistema quebrado — e num sistema
 * regulado ela levanta a pergunta pior: *o que deveria estar aí e não está?*.
 * A pendência está escrita em `docs/features/1-producao/1.3-consulta-de-lotes`.
 *
 * <p>⚠️ **`etiqueta`, no singular, não é descuido.** É o segmento que já existia
 * em `/producao/materias-primas/:id/etiqueta` — o endereço que o PO usa e já
 * mandou duas vezes. Mantê-lo faz a URL antiga **ser** o endereço da aba, em vez
 * de virar um redirecionamento que alguém apaga daqui a seis meses.
 */
const ABAS = ["resumo", "volumes", "etiqueta"] as const;
type IdDeAba = (typeof ABAS)[number];

const PADRAO: IdDeAba = "resumo";

function ehAba(valor: string | undefined): valor is IdDeAba {
  return valor !== undefined && (ABAS as readonly string[]).includes(valor);
}

/**
 * O LOTE, aberto pela lista — com abas.
 *
 * <h2>O que esta tela resolve</h2>
 *
 * <p>Queixa do PO (#36): a tela de etiquetas *"está mal pensada de UI/UX"*, e o
 * que ele pediu foram **abas — do lote**. Antes, resumo e etiquetas eram duas
 * páginas: para ver a validade antes de imprimir, voltava-se à lista, procurava-
 * se a linha e abria-se outra tela. A ida e volta não era só incômodo — quem
 * confere material perde de vista o código do lote no caminho, e passa a decidir
 * de memória.
 *
 * <h2>⚠️ Ela NÃO é a ficha que o QR abre — e a separação é regulatória</h2>
 *
 * <pre>
 *   /producao/materias-primas/:id/resumo?empresa=    ← ESTA tela (pela lista)
 *   /producao/materias-primas/:id/volumes?empresa=
 *   /producao/materias-primas/:id/etiqueta?empresa=
 *
 *   /producao/materias-primas/lote/:codigo           ← `PaginaLote`, sem abas
 *   /producao/materias-primas/volume/:token          ← idem — o QR do tambor
 * </pre>
 *
 * <p>As duas de baixo continuam **exatamente** como estavam, e isso é a
 * <b>R1</b> e a <b>R10</b> da feature 1.2.3: a ficha que o QR abre é de
 * consulta, e *"o caminho para a etiqueta não volta para cá — um botão de
 * imprimir a um toque de quem escaneou um tambor é como uma segunda etiqueta do
 * mesmo lote passa a circular sem ninguém pedir"*. Dar abas àquela tela poria a
 * emissão a um toque do corredor. Aqui não: quem chega por esta rota veio da
 * lista, com o CNPJ escolhido, na mesa.
 *
 * <p>Elas também são **endereços impressos** (ADR-0006): mudá-las quebraria
 * adesivos que já estão colados em tambores.
 *
 * <h2>Cada aba tem endereço, e a URL antiga é um deles</h2>
 *
 * <p>Recarregar não perde a aba, e o link de uma aba específica se manda. O
 * endereço que o PO guardou —
 * `/producao/materias-primas/&lt;id&gt;/etiqueta?empresa=&lt;id&gt;` — **não foi
 * redirecionado**: ele é, literalmente, o endereço da aba de etiquetas. Um
 * endereço guardado que para de funcionar é o pior resultado possível desta
 * tarefa, e a forma mais segura de não quebrá-lo é não mexer nele.
 *
 * <p>`?empresa=` viaja em toda troca de aba, e não é adorno: `GET /lotes/{id}`
 * e `GET /lotes/{id}/volumes` exigem a empresa, e o servidor confere. Ela vem da
 * **linha em que se clicou** — no consolidado não há empresa no cabeçalho.
 *
 * <h2>O cabeçalho fica ACIMA das abas</h2>
 *
 * <p>Código, empresa e situação em toda aba, sem exceção. Não é enfeite de
 * composição: é a conferência de que se está no lote e no CNPJ certos, e o
 * código do lote é sequencial **por empresa** — `MP-0001` existe nas duas
 * produtoras (ADR-0005 §10). Trocar de aba não pode ser um momento em que a
 * pessoa deixa de ver de quem é o material que ela está prestes a etiquetar.
 */
export function LoteEmAbas() {
  const { id, aba } = useParams<{ id: string; aba?: string }>();
  const [parametros] = useSearchParams();
  const navegar = useNavigate();
  const { atual, lista, escolha, escolher } = usarEmpresas();

  /*
   * A empresa vem da ROTA. É a mesma leitura que `PaginaEtiquetas` faz desde a
   * #27, e pelo mesmo motivo: a lista de lotes deixou de exigir uma empresa
   * escolhida, e ali `atual` é nulo. Quem sabe de qual empresa é aquele lote é
   * a linha em que se clicou.
   */
  const empresaDoLote = parametros.get("empresa") ?? atual?.id ?? null;
  const consulta = parametros.toString() === "" ? "" : `?${parametros.toString()}`;

  const [lote, definirLote] = useState<Lote | null>(null);
  const [erro, definirErro] = useState<string | null>(null);

  useEffect(() => {
    if (empresaDoLote === null || id === undefined) return;
    definirErro(null);
    producao
      .lote(empresaDoLote, id)
      .then(definirLote)
      .catch(() => definirErro("Não foi possível carregar o lote."));
  }, [empresaDoLote, id]);

  /*
   * ⚠️ O ENDEREÇO SEMPRE NOMEIA A ABA — e por isso este redirecionamento.
   *
   * `/…/:id` sem seção e `/…/:id/historico` (que não existe) cairiam no resumo
   * com a URL dizendo outra coisa: a aba marcada e o endereço discordariam, e
   * é o endereço que a pessoa copia e manda. `replace` porque o endereço
   * incompleto não é um passo de navegação — voltar tem de sair do lote, não
   * devolver a um endereço que já se corrigiu.
   *
   * Vem DEPOIS dos hooks: retorno antecipado acima de um `useEffect` muda a
   * ordem dos hooks entre renderizações.
   */
  if (!ehAba(aba)) {
    return <Navigate to={`/producao/materias-primas/${id}/${PADRAO}${consulta}`} replace />;
  }
  const atualDaAba: IdDeAba = aba;

  /*
   * ┌─ 🔴 O ENDEREÇO SE CURA: SE A EMPRESA VEIO DO CABEÇALHO, ELA VAI PARA A ┐
   * │    URL                                                                 │
   * │                                                                        │
   * │ `empresaDoLote` aceita duas origens — a query e a empresa escolhida no │
   * │ cabeçalho — e só a primeira sobrevive a um `Ctrl+C`. Sem esta linha, a │
   * │ tela abria normalmente para quem tinha a empresa escolhida, e o        │
   * │ endereço que essa pessoa copiasse da barra chegaria ao colega SEM a    │
   * │ query, caindo na parede tratada logo abaixo. O defeito não aparece     │
   * │ para quem o produz: ele aparece para quem recebe o link.               │
   * │                                                                        │
   * │ `replace` porque completar o próprio endereço não é um passo de        │
   * │ navegação — voltar tem de sair do lote, e não devolver ao endereço     │
   * │ incompleto que acabou de ser corrigido. É a mesma decisão do           │
   * │ redirecionamento de aba, algumas linhas acima.                          │
   * └────────────────────────────────────────────────────────────────────────┘
   */
  if (parametros.get("empresa") === null && atual !== null) {
    const completo = new URLSearchParams(parametros);
    completo.set("empresa", atual.id);
    return (
      <Navigate
        to={`/producao/materias-primas/${id}/${atualDaAba}?${completo.toString()}`}
        replace
      />
    );
  }

  if (empresaDoLote === null) {
    /*
     * ┌─ 🔴 ISTO ERA UM BECO SEM SAÍDA, E O ENDEREÇO QUE CAI AQUI É O QUE A ┐
     * │    #36 PROMETE MANTER VIVO                                          │
     * │                                                                     │
     * │ A tela dizia "Escolha a empresa para ver este lote." — e **não      │
     * │ havia seletor de empresa nesta tela**. Confirmado por controle: o   │
     * │ seletor foi procurado depois de a página carregar e nenhuma opção   │
     * │ existia. Quem abrisse                                               │
     * │ `/producao/materias-primas/<id>/etiqueta` sem `?empresa=` — o       │
     * │ endereço que o PO guardou, e o que sobra quando alguém copia um     │
     * │ link de um lugar que corta a query — encontrava uma frase           │
     * │ imperativa mandando fazer algo que a tela não oferecia.             │
     * │                                                                     │
     * │ Instrução sem o controle que a cumpre é pior que erro: erro manda   │
     * │ procurar ajuda, e isto manda procurar um botão que não existe.      │
     * │                                                                     │
     * │ ⚠️ SÓ PRODUTORAS. Material pertence a quem produz; a distribuidora  │
     * │ não tem processo produtivo para consumir insumo, e oferecê-la aqui  │
     * │ seria oferecer um caminho que o servidor recusa (ADR-0005).         │
     * │                                                                     │
     * │ ⚠️ E ESCOLHER AQUI NÃO ADIVINHA NADA. A escolha vai ao servidor,    │
     * │ que confere se o lote é daquele CNPJ — empresa errada devolve a     │
     * │ falha de carregamento, não a ficha de outro material. O sistema não │
     * │ escolhe a empresa por ninguém (ADR-0005 §10); o que ele passa a     │
     * │ fazer é deixar a pessoa escolher.                                    │
     * └─────────────────────────────────────────────────────────────────────┘
     */
    return (
      <Alerta tom="atencao" titulo="Escolha a empresa para ver este lote">
        <p className="lote-abas__saida">
          O endereço deste lote não diz de qual empresa ele é, e material
          pertence a um <strong>CNPJ</strong> — o sistema não escolhe por você.
          Escolha abaixo, ou volte à lista e abra o lote pela linha dele, que já
          leva a empresa junto.
        </p>
        <SeletorDeEmpresa
          empresas={lista
            .filter((e) => e.papel === "PRODUTORA")
            .map((e) => ({ id: e.id, nome: e.razaoSocial, apelido: e.apelido, papel: e.papel }))}
          selecionada={escolha ?? ""}
          aoSelecionar={escolher}
        />
        <p className="lote-abas__saida">
          <Botao aparencia="texto" onClick={() => navegar(ROTAS.lotes)}>
            Voltar à lista de lotes
          </Botao>
        </p>
      </Alerta>
    );
  }
  if (erro !== null && lote === null) {
    return <Alerta tom="erro">{erro}</Alerta>;
  }
  if (lote === null) {
    return <p role="status">Carregando lote…</p>;
  }

  const abas: Aba[] = [
    { id: "resumo", rotulo: "Resumo", href: endereco("resumo") },
    {
      id: "volumes",
      rotulo: "Volumes",
      // A contagem vem do LOTE, que já está carregado — não de uma segunda
      // chamada só para escrever um número na aba.
      nota: String(lote.volumes),
      href: endereco("volumes"),
    },
    { id: "etiqueta", rotulo: "Etiquetas", href: endereco("etiqueta") },
  ];

  function endereco(destino: IdDeAba): string {
    return `/producao/materias-primas/${id}/${destino}${consulta}`;
  }

  return (
    /* ⚠️ O MODIFICADOR NÃO É COSMÉTICO: é ele que liga a lista de PERMITIDOS
       do `@media print` — `.lote-abas--etiqueta > *:not(.ui-abas__painel)`.
       Só a aba de etiquetas manda documento para a impressora; nas outras, o
       cabeçalho do lote deve imprimir junto, senão uma impressão do resumo
       sairia sem dizer de qual lote e de qual CNPJ ela é. Ver `app.css`. */
    /* Tela DENSA, e o tipo é da TELA — não da aba. As três seções (resumo,
       volumes, etiquetas) são a mesma tela com o mesmo lote; declarar por aba
       faria a página mudar de largura ao trocar de seção, o que se lê como a
       tela se remontando sozinha. Ver "A LEI DE LARGURA" em `app.css`. */
    <div
      className={`lote-abas${atualDaAba === "etiqueta" ? " lote-abas--etiqueta" : ""}`}
      data-largura="densa"
    >
      {/* ── O CABEÇALHO DO LOTE — acima das abas, em todas elas ──────────
          Ele é o `<h1>` da tela. As abas não têm título próprio de propósito:
          um `<h1>` por aba faria a tela mudar de nome ao trocar de seção, e o
          nome dela é o lote.

          ⚠️ Ele NÃO é `position: sticky`. "Sempre à vista" aqui significa "em
          toda aba", que é o que a troca de seção põe em risco — e uma faixa
          grudada no topo custa altura justamente no aparelho em que ela é mais
          cara. Se um dia a rolagem for longa o bastante para o código do lote
          sumir, isto se revisita com medida. */}
      <header className="lote-abas__cabeca">
        {/* ── O CAMINHO SUBSTITUIU DUAS PEÇAS ───────────────────────────
            Aqui havia um botão `← Voltar aos lotes` E um chapéu
            `Produção · Matérias-primas`. Eram a mesma informação partida em
            duas: uma dizia PARA ONDE voltar sem dizer onde se está, a outra
            dizia onde se está sem levar a lugar nenhum.

            O caminho diz as duas coisas de uma vez, e desce mais um degrau:
            o CÓDIGO DO LOTE, que é o único que a rota não sabe — ela carrega
            `:id`, não `MP-COP-382684`. É a folha declarada, e folha é o
            degrau atual: ela não vira link nem quando erra.

            ⚠️ A altura NÃO cresceu: `← Voltar aos lotes` era um
            `.ui-botao--texto`, que já sobe para `var(--alvo-toque)` abaixo de
            52rem — os mesmos 44px, dizendo o caminho inteiro em vez de um
            destino. */}
        <Caminho passos={caminhoDaRota(ROTAS.lotes, lote.codigo)} aoNavegar={navegar} />

        {/* Em mono e grande, do jeito que sai na etiqueta: é assim que se
            confere que a tela e o papel falam do mesmo material. */}
        <h1 className="lote-abas__codigo">{lote.codigo}</h1>

        {/* A EMPRESA e a SITUAÇÃO na mesma fita, e as duas são o que pode
            FAZER PARAR: material do CNPJ errado e material que ainda não foi
            liberado. Rótulo e cor da situação saem do mesmo mapa que a lista
            de lotes usa — mapa duplicado é mapa que diverge.

            A empresa é a DO LOTE, resolvida no servidor, nunca a do seletor do
            cabeçalho (ADR-0005 §10). */}
        <p className="lote-abas__selos">
          <SeloDeEmpresa empresa={lote.empresa} />
          <Selo situacao={TOM_DA_SITUACAO[lote.situacao]}>
            {ROTULO_DA_SITUACAO[lote.situacao]}
          </Selo>
        </p>

        <p className="lote-abas__material">
          {lote.materiaPrima}
          <span className="lote-abas__mp">{lote.codigoMateriaPrima}</span>
        </p>
      </header>

      <Abas
        rotulo="Seções do lote"
        abas={abas}
        atual={atualDaAba}
        /* `replace` não: trocar de aba é navegar, e o botão "voltar" do
           navegador tem de desfazer isso. Quem chegou pela lista e abriu três
           abas espera três passos de volta, não um salto para a lista. */
        aoEscolher={(destino) => navegar(endereco(destino as IdDeAba))}
      >
        {atualDaAba === "resumo" && <AbaResumo lote={lote} />}
        {atualDaAba === "volumes" && (
          <AbaVolumes empresaId={empresaDoLote} loteId={lote.id} unidade={lote.unidade} />
        )}
        {/* ⚠️ A tela de etiquetas entra INTEIRA, e ela continua carregando o
            lote e os volumes dela. Passar o lote daqui pouparia uma chamada e
            custaria o que não vale: `PaginaEtiquetas` deixaria de funcionar
            sozinha, e os vinte e quatro casos de `emissao.teste.tsx` — que são o que
            impede a #34 de regredir — teriam de ser remontados junto. A
            requisição repetida está registrada como pendência. */}
        {atualDaAba === "etiqueta" && <PaginaEtiquetas />}
      </Abas>
    </div>
  );
}
